import {
  ROAD_VEHICLE_TUNING,
  VILNIUS_ROAD_VEHICLES,
  type RoadVehicleSeatRole,
  type RoadVehicleSpec,
} from "../../config/vehicles.ts";
import type { PlayerId } from "../../core/ids.ts";
import { wrapRadians } from "../../core/math.ts";
import type { InputIntent } from "../../input/intents.ts";
import type { GameState } from "../state.ts";

export const ROAD_VEHICLE_FLEET_SCHEMA_VERSION = 4 as const;

export type RoadVehicleSurfaceKind = "road" | "hardscape" | "terrain";
export type RoadVehicleDoorSide = "left" | "right";

export interface RoadVehicleEnvironment {
  readonly wetness01: number;
}

export interface RoadVehicleState {
  readonly id: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
  readonly pitchRadians: number;
  readonly rollRadians: number;
  readonly speedMps: number;
  readonly steeringRadians: number;
  readonly longitudinalAccelerationMps2: number;
  readonly wheelRotationRadians: number;
  readonly visualPitchRadians: number;
  readonly visualRollRadians: number;
  readonly suspensionCompressionM: number;
  readonly surfaceKind: RoadVehicleSurfaceKind;
  readonly surfaceId: string | null;
  readonly headlightsOn: boolean;
  readonly brakeLightsOn: boolean;
  readonly reverseLightsOn: boolean;
  readonly leftIndicatorOn: boolean;
  readonly rightIndicatorOn: boolean;
  readonly beaconOn: boolean;
  readonly sirenOn: boolean;
  readonly hornActive: boolean;
  readonly lookBehindActive: boolean;
  readonly handbrakeActive: boolean;
  readonly activeDoorSide: RoadVehicleDoorSide | null;
  readonly doorOpenSecondsRemaining: number;
  readonly impactIntensity01: number;
  readonly damage01: number;
  readonly lastSafePosition: Readonly<{ x: number; y: number; z: number }>;
  readonly lastSafeYawRadians: number;
  readonly occupantBySeatId: Readonly<Record<string, PlayerId>>;
}

export interface RoadVehicleMount {
  readonly vehicleId: string;
  readonly seatId: string;
  readonly transition?: Readonly<{
    kind: "entering" | "exiting";
    secondsRemaining: number;
    startPosition: Readonly<{ x: number; y: number; z: number }>;
    endPosition: Readonly<{ x: number; y: number; z: number }>;
  }>;
}

export interface RoadVehicleFleetState {
  readonly schemaVersion: typeof ROAD_VEHICLE_FLEET_SCHEMA_VERSION;
  readonly tick: number;
  readonly vehiclesById: Readonly<Record<string, RoadVehicleState>>;
  readonly mountByPlayerId: Readonly<Record<PlayerId, RoadVehicleMount>>;
  /** Source-positioned cars currently streamed or promoted by player use. */
  readonly dynamicSpecsById: Readonly<Record<string, RoadVehicleSpec>>;
}

export interface RoadVehiclePresentation {
  readonly id: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
  readonly pitchRadians: number;
  readonly rollRadians: number;
  readonly speedMps: number;
  readonly steeringRadians: number;
  readonly longitudinalAccelerationMps2: number;
  readonly wheelRotationRadians: number;
  readonly visualPitchRadians: number;
  readonly visualRollRadians: number;
  readonly suspensionCompressionM: number;
  readonly surfaceKind: RoadVehicleSurfaceKind;
  readonly headlightsOn: boolean;
  readonly brakeLightsOn: boolean;
  readonly reverseLightsOn: boolean;
  readonly leftIndicatorOn: boolean;
  readonly rightIndicatorOn: boolean;
  readonly beaconOn: boolean;
  readonly sirenOn: boolean;
  readonly hornActive: boolean;
  readonly lookBehindActive: boolean;
  readonly handbrakeActive: boolean;
  readonly activeDoorSide: RoadVehicleDoorSide | null;
  readonly doorOpen01: number;
  readonly indicatorPhase01: number;
  readonly impactIntensity01: number;
  readonly damage01: number;
}

export interface RoadVehicleQueries {
  resolveMotion(request: Readonly<{
    vehicleId: string;
    currentPosition: Readonly<{ x: number; y: number; z: number }>;
    proposedPosition: Readonly<{ x: number; y: number; z: number }>;
    currentYawRadians: number;
    proposedYawRadians: number;
    currentPitchRadians: number;
    currentRollRadians: number;
    maximumStepHeightM: number;
    maximumGroundTiltRadians: number;
    currentSurfaceKind: RoadVehicleSurfaceKind;
    currentSurfaceId: string | null;
  }>): Readonly<{
    position: Readonly<{ x: number; y: number; z: number }>;
    yawRadians: number;
    pitchRadians: number;
    rollRadians: number;
    blocked: boolean;
    surfaceKind?: RoadVehicleSurfaceKind;
    surfaceId?: string | null;
  }>;
  resolveExit(request: Readonly<{
    vehicleId: string;
    candidates: readonly Readonly<{ x: number; y: number; z: number }>[];
  }>): Readonly<{ x: number; y: number; z: number }> | null;
  recover(request: Readonly<{
    vehicleId: string;
    position: Readonly<{ x: number; y: number; z: number }>;
    yawRadians: number;
  }>): void;
}

export interface RoadVehicleFleetStepResult {
  readonly state: RoadVehicleFleetState;
  readonly transition:
    | "entered"
    | "exited"
    | "seat-changed"
    | "recovered"
    | null;
  readonly interactionConsumed: boolean;
  readonly blockedExit: boolean;
  readonly playerOverride: Readonly<{
    position: Readonly<{ x: number; y: number; z: number }>;
    facingYaw: number;
    surfaceId: string;
    vehicleId: string | null;
    seatRole: RoadVehicleSeatRole | null;
  }> | null;
}

const NO_QUERIES: RoadVehicleQueries = {
  resolveMotion: (request) => ({
    position: request.proposedPosition,
    yawRadians: request.proposedYawRadians,
    pitchRadians: request.currentPitchRadians,
    rollRadians: request.currentRollRadians,
    blocked: false,
    surfaceKind: request.currentSurfaceKind,
    surfaceId: request.currentSurfaceId,
  }),
  resolveExit: (request) => request.candidates[0] ?? null,
  recover: () => {},
};

const DRY_ROAD_ENVIRONMENT: RoadVehicleEnvironment = Object.freeze({
  wetness01: 0,
});

export function createInitialRoadVehicleFleetState(): RoadVehicleFleetState {
  const vehiclesById: Record<string, RoadVehicleState> = {};
  for (const spec of VILNIUS_ROAD_VEHICLES) {
    vehiclesById[spec.id] = {
      id: spec.id,
      position: { ...spec.initialPosition },
      yawRadians: spec.initialYawRadians,
      pitchRadians: 0,
      rollRadians: 0,
      speedMps: 0,
      steeringRadians: 0,
      longitudinalAccelerationMps2: 0,
      wheelRotationRadians: 0,
      visualPitchRadians: 0,
      visualRollRadians: 0,
      suspensionCompressionM: 0,
      surfaceKind: "road",
      surfaceId: null,
      headlightsOn: false,
      brakeLightsOn: false,
      reverseLightsOn: false,
      leftIndicatorOn: false,
      rightIndicatorOn: false,
      beaconOn: false,
      sirenOn: false,
      hornActive: false,
      lookBehindActive: false,
      handbrakeActive: false,
      activeDoorSide: null,
      doorOpenSecondsRemaining: 0,
      impactIntensity01: 0,
      damage01: 0,
      lastSafePosition: { ...spec.initialPosition },
      lastSafeYawRadians: spec.initialYawRadians,
      occupantBySeatId: {},
    };
  }
  return {
    schemaVersion: ROAD_VEHICLE_FLEET_SCHEMA_VERSION,
    tick: 0,
    vehiclesById,
    mountByPlayerId: {},
    dynamicSpecsById: {},
  };
}

export function createRoadVehicleState(
  spec: Readonly<RoadVehicleSpec>,
): RoadVehicleState {
  return {
    id: spec.id,
    position: { ...spec.initialPosition },
    yawRadians: spec.initialYawRadians,
    pitchRadians: 0,
    rollRadians: 0,
    speedMps: 0,
    steeringRadians: 0,
    longitudinalAccelerationMps2: 0,
    wheelRotationRadians: 0,
    visualPitchRadians: 0,
    visualRollRadians: 0,
    suspensionCompressionM: 0,
    surfaceKind: "road",
    surfaceId: null,
    headlightsOn: false,
    brakeLightsOn: false,
    reverseLightsOn: false,
    leftIndicatorOn: false,
    rightIndicatorOn: false,
    beaconOn: false,
    sirenOn: false,
    hornActive: false,
    lookBehindActive: false,
    handbrakeActive: false,
    activeDoorSide: null,
    doorOpenSecondsRemaining: 0,
    impactIntensity01: 0,
    damage01: 0,
    lastSafePosition: { ...spec.initialPosition },
    lastSafeYawRadians: spec.initialYawRadians,
    occupantBySeatId: {},
  };
}

export function addDynamicRoadVehicleSpecs(
  state: Readonly<RoadVehicleFleetState>,
  specs: readonly Readonly<RoadVehicleSpec>[],
): RoadVehicleFleetState {
  if (specs.length === 0) return state;
  const dynamicSpecsById = { ...state.dynamicSpecsById };
  const vehiclesById = { ...state.vehiclesById };
  for (const spec of specs) {
    dynamicSpecsById[spec.id] = spec;
    vehiclesById[spec.id] ??= createRoadVehicleState(spec);
  }
  return { ...state, dynamicSpecsById, vehiclesById };
}

export function removeDynamicRoadVehicles(
  state: Readonly<RoadVehicleFleetState>,
  vehicleIds: ReadonlySet<string>,
): RoadVehicleFleetState {
  if (vehicleIds.size === 0) return state;
  const mountedIds = new Set(
    Object.values(state.mountByPlayerId).map((mount) => mount.vehicleId),
  );
  const dynamicSpecsById = { ...state.dynamicSpecsById };
  const vehiclesById = { ...state.vehiclesById };
  for (const vehicleId of vehicleIds) {
    if (mountedIds.has(vehicleId)) continue;
    delete dynamicSpecsById[vehicleId];
    delete vehiclesById[vehicleId];
  }
  return { ...state, dynamicSpecsById, vehiclesById };
}

export function roadVehicleSpecs(
  state: Readonly<RoadVehicleFleetState>,
): readonly Readonly<RoadVehicleSpec>[] {
  return [...VILNIUS_ROAD_VEHICLES, ...Object.values(state.dynamicSpecsById)];
}

export function roadVehicleSpecFor(
  state: Readonly<RoadVehicleFleetState>,
  id: string,
): RoadVehicleSpec {
  const spec = state.dynamicSpecsById[id] ??
    VILNIUS_ROAD_VEHICLES.find((entry) => entry.id === id);
  if (spec === undefined) throw new Error(`Road vehicle spec ${id} is missing`);
  return spec;
}

export function clearRoadVehicleOccupancy(
  state: Readonly<RoadVehicleFleetState>,
): RoadVehicleFleetState {
  return {
    ...state,
    mountByPlayerId: {},
    vehiclesById: Object.fromEntries(
      Object.values(state.vehiclesById).map((vehicle) => [
        vehicle.id,
        {
          ...vehicle,
          speedMps: 0,
          steeringRadians: 0,
          longitudinalAccelerationMps2: 0,
          hornActive: false,
          lookBehindActive: false,
          handbrakeActive: false,
          occupantBySeatId: {},
        },
      ]),
    ),
  };
}

export function stepRoadVehicleFleet(
  state: Readonly<RoadVehicleFleetState>,
  intent: Readonly<InputIntent>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  deltaSeconds: number,
  queries: Readonly<RoadVehicleQueries> = NO_QUERIES,
  environment: Readonly<RoadVehicleEnvironment> = DRY_ROAD_ENVIRONMENT,
): RoadVehicleFleetStepResult {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
    throw new Error("Road vehicle deltaSeconds must be positive");
  }
  if (!Number.isFinite(environment.wetness01)) {
    throw new Error("Road vehicle wetness must be finite");
  }
  state = advanceTransientVehicleState(state, deltaSeconds);
  const mount = state.mountByPlayerId[intent.playerId];
  if (mount === undefined) {
    const candidate = nearestEnterableVehicle(state, playerPosition);
    if (!intent.interactPressed || candidate === null) {
      return unchanged(state, intent.tick);
    }
    const seat = nearestAvailableSeat(candidate.spec, candidate.vehicle, playerPosition);
    if (seat === null) {
      return unchanged(state, intent.tick);
    }
    const vehicle = {
      ...candidate.vehicle,
      activeDoorSide: sideForLocalX(seat.localPosition.x),
      doorOpenSecondsRemaining:
        ROAD_VEHICLE_TUNING.occupantTransitionSeconds +
        ROAD_VEHICLE_TUNING.doorTransitionSeconds,
      occupantBySeatId: {
        ...candidate.vehicle.occupantBySeatId,
        [seat.id]: intent.playerId,
      },
    };
    const next: RoadVehicleFleetState = {
      ...state,
      tick: intent.tick,
      vehiclesById: { ...state.vehiclesById, [vehicle.id]: vehicle },
      mountByPlayerId: {
        ...state.mountByPlayerId,
        [intent.playerId]: {
          vehicleId: vehicle.id,
          seatId: seat.id,
          transition: {
            kind: "entering" as const,
            secondsRemaining: ROAD_VEHICLE_TUNING.occupantTransitionSeconds,
            startPosition: { ...playerPosition },
            endPosition: localToWorld(
              vehicle.position,
              vehicle.yawRadians,
              seat.localPosition,
            ),
          },
        },
      },
    };
    return resultForMounted(next, vehicle, candidate.spec, seat.id, "entered", true);
  }

  const spec = roadVehicleSpecFor(state, mount.vehicleId);
  let vehicle = state.vehiclesById[mount.vehicleId];
  if (vehicle === undefined) {
    throw new Error(`Mounted road vehicle ${mount.vehicleId} is missing`);
  }
  let seatId = mount.seatId;
  let transition: RoadVehicleFleetStepResult["transition"] = null;

  if (mount.transition !== undefined) {
    const secondsRemaining = Math.max(
      0,
      mount.transition.secondsRemaining - deltaSeconds,
    );
    const nextMount: RoadVehicleMount = secondsRemaining > 0
      ? {
          ...mount,
          transition: { ...mount.transition, secondsRemaining },
        }
      : { vehicleId: mount.vehicleId, seatId: mount.seatId };
    state = {
      ...state,
      tick: intent.tick,
      mountByPlayerId: {
        ...state.mountByPlayerId,
        [intent.playerId]: nextMount,
      },
    };
    if (secondsRemaining > 0) {
      return resultForMounted(
        state,
        vehicle,
        spec,
        seatId,
        null,
        false,
      );
    }
    if (mount.transition.kind === "exiting") {
      const occupants = { ...vehicle.occupantBySeatId };
      delete occupants[seatId];
      vehicle = { ...vehicle, occupantBySeatId: occupants };
      const mounts = { ...state.mountByPlayerId };
      delete mounts[intent.playerId];
      return {
        state: {
          ...state,
          vehiclesById: { ...state.vehiclesById, [vehicle.id]: vehicle },
          mountByPlayerId: mounts,
        },
        transition: "exited",
        interactionConsumed: false,
        blockedExit: false,
        playerOverride: {
          position: { ...mount.transition.endPosition },
          facingYaw: vehicle.yawRadians,
          surfaceId: `road-exit:${vehicle.id}`,
          vehicleId: null,
          seatRole: null,
        },
      };
    }
  }

  if (intent.cycleSeatPressed && Math.abs(vehicle.speedMps) <= 0.05) {
    const nextSeat = nextAvailableSeat(spec, vehicle, seatId);
    if (nextSeat !== null && nextSeat.id !== seatId) {
      const occupants = { ...vehicle.occupantBySeatId };
      delete occupants[seatId];
      occupants[nextSeat.id] = intent.playerId;
      seatId = nextSeat.id;
      vehicle = { ...vehicle, occupantBySeatId: occupants };
      transition = "seat-changed";
    }
  }

  if (intent.interactPressed && Math.abs(vehicle.speedMps) <= ROAD_VEHICLE_TUNING.minimumExitSpeedMps) {
    const exitOrigin = vehicle.position;
    const exitYaw = vehicle.yawRadians;
    const exit = queries.resolveExit({
      vehicleId: vehicle.id,
      candidates: spec.exits.map((candidate) =>
        localToWorld(exitOrigin, exitYaw, candidate.localPosition)
      ),
    });
    if (exit === null) {
      const next = updateMountedState(state, intent.playerId, vehicle, seatId, intent.tick);
      return {
        ...resultForMounted(next, vehicle, spec, seatId, transition, true),
        blockedExit: true,
      };
    }
    vehicle = {
      ...vehicle,
      speedMps: 0,
      steeringRadians: 0,
      longitudinalAccelerationMps2: 0,
      activeDoorSide: sideForLocalX(seatIdPositionX(spec, seatId)),
      doorOpenSecondsRemaining:
        ROAD_VEHICLE_TUNING.occupantTransitionSeconds +
        ROAD_VEHICLE_TUNING.doorTransitionSeconds,
    };
    const seat = spec.seats.find((entry) => entry.id === seatId);
    if (seat === undefined) throw new Error(`Seat ${seatId} is missing from ${spec.id}`);
    const next: RoadVehicleFleetState = {
      ...state,
      tick: intent.tick,
      vehiclesById: { ...state.vehiclesById, [vehicle.id]: vehicle },
      mountByPlayerId: {
        ...state.mountByPlayerId,
        [intent.playerId]: {
          vehicleId: vehicle.id,
          seatId,
          transition: {
            kind: "exiting",
            secondsRemaining: ROAD_VEHICLE_TUNING.occupantTransitionSeconds,
            startPosition: localToWorld(
              vehicle.position,
              vehicle.yawRadians,
              seat.localPosition,
            ),
            endPosition: { ...exit },
          },
        },
      },
    };
    return resultForMounted(next, vehicle, spec, seatId, null, true);
  }

  const seat = spec.seats.find((entry) => entry.id === seatId);
  if (seat?.role === "driver") {
    vehicle = applyVehicleControlToggles(vehicle, spec, intent);
    if (
      intent.recoverVehiclePressed &&
      Math.abs(vehicle.speedMps) <= ROAD_VEHICLE_TUNING.recoveryMaximumSpeedMps
    ) {
      vehicle = recoverVehicle(vehicle, queries);
      transition = "recovered";
    } else {
      vehicle = driveVehicle(
        vehicle,
        spec,
        intent,
        deltaSeconds,
        queries,
        environment,
      );
    }
  }
  const next = updateMountedState(state, intent.playerId, vehicle, seatId, intent.tick);
  return resultForMounted(
    next,
    vehicle,
    spec,
    seatId,
    transition,
    intent.interactPressed || intent.cycleSeatPressed === true,
  );
}

function advanceTransientVehicleState(
  state: Readonly<RoadVehicleFleetState>,
  deltaSeconds: number,
): RoadVehicleFleetState {
  return {
    ...state,
    vehiclesById: Object.fromEntries(
      Object.values(state.vehiclesById).map((vehicle) => {
        const doorOpenSecondsRemaining = Math.max(
          0,
          vehicle.doorOpenSecondsRemaining - deltaSeconds,
        );
        return [vehicle.id, {
          ...vehicle,
          doorOpenSecondsRemaining,
          activeDoorSide: doorOpenSecondsRemaining > 0
            ? vehicle.activeDoorSide
            : null,
          impactIntensity01: Math.max(
            0,
            vehicle.impactIntensity01 -
              deltaSeconds / ROAD_VEHICLE_TUNING.impactFeedbackSeconds,
          ),
        }];
      }),
    ),
  };
}

function applyVehicleControlToggles(
  vehicle: Readonly<RoadVehicleState>,
  spec: Readonly<RoadVehicleSpec>,
  intent: Readonly<InputIntent>,
): RoadVehicleState {
  const leftIndicatorOn = intent.toggleLeftIndicatorPressed
    ? !vehicle.leftIndicatorOn
    : intent.toggleRightIndicatorPressed
      ? false
      : vehicle.leftIndicatorOn;
  const rightIndicatorOn = intent.toggleRightIndicatorPressed
    ? !vehicle.rightIndicatorOn
    : intent.toggleLeftIndicatorPressed
      ? false
      : vehicle.rightIndicatorOn;
  return {
    ...vehicle,
    headlightsOn: intent.toggleHeadlightsPressed
      ? !vehicle.headlightsOn
      : vehicle.headlightsOn,
    leftIndicatorOn,
    rightIndicatorOn,
    beaconOn:
      spec.hasEmergencyEquipment && intent.toggleBeaconPressed
        ? !vehicle.beaconOn
        : vehicle.beaconOn,
    sirenOn:
      spec.hasEmergencyEquipment && intent.toggleSirenPressed
        ? !vehicle.sirenOn
        : vehicle.sirenOn,
  };
}

function recoverVehicle(
  vehicle: Readonly<RoadVehicleState>,
  queries: Readonly<RoadVehicleQueries>,
): RoadVehicleState {
  const position = {
    ...vehicle.lastSafePosition,
    y: vehicle.lastSafePosition.y + ROAD_VEHICLE_TUNING.recoveryLiftM,
  };
  queries.recover({
    vehicleId: vehicle.id,
    position,
    yawRadians: vehicle.lastSafeYawRadians,
  });
  return {
    ...vehicle,
    position,
    yawRadians: vehicle.lastSafeYawRadians,
    pitchRadians: 0,
    rollRadians: 0,
    speedMps: 0,
    steeringRadians: 0,
    longitudinalAccelerationMps2: 0,
    visualPitchRadians: 0,
    visualRollRadians: 0,
    suspensionCompressionM: 0,
    brakeLightsOn: false,
    reverseLightsOn: false,
    hornActive: false,
    lookBehindActive: false,
    handbrakeActive: false,
    impactIntensity01: 0,
    damage01: Math.max(
      0,
      vehicle.damage01 - ROAD_VEHICLE_TUNING.recoveryDamageReduction01,
    ),
  };
}

function driveVehicle(
  vehicle: Readonly<RoadVehicleState>,
  spec: Readonly<RoadVehicleSpec>,
  intent: Readonly<InputIntent>,
  deltaSeconds: number,
  queries: Readonly<RoadVehicleQueries>,
  environment: Readonly<RoadVehicleEnvironment>,
): RoadVehicleState {
  const tuning = ROAD_VEHICLE_TUNING.motionByClass[spec.vehicleClass];
  const wetness01 = clamp(environment.wetness01, 0, 1);
  const surfaceGrip = vehicle.surfaceKind === "road"
    ? ROAD_VEHICLE_TUNING.roadGripScale
    : vehicle.surfaceKind === "hardscape"
      ? ROAD_VEHICLE_TUNING.hardscapeGripScale
      : ROAD_VEHICLE_TUNING.terrainGripScale;
  const wetGrip = lerp(1, ROAD_VEHICLE_TUNING.fullWetGripScale, wetness01);
  const gripScale = surfaceGrip * wetGrip;
  const steeringFade01 = smoothstep(
    tuning.steeringFadeStartSpeedMps,
    tuning.steeringFadeEndSpeedMps,
    Math.abs(vehicle.speedMps),
  );
  const steeringLimit =
    tuning.maximumSteerRadians *
    lerp(1, tuning.highSpeedSteeringScale, steeringFade01) *
    gripScale;
  // Positive input is player-right, while this world's yaw convention turns
  // right by decreasing yaw.
  const desiredSteering = -clamp(intent.moveX, -1, 1) * steeringLimit;
  const steeringResponse = Math.abs(desiredSteering) <= Number.EPSILON
    ? tuning.steeringSelfCenterResponsePerSecond
    : tuning.steeringResponseRadiansPerSecond;
  const steeringRadians = smoothToward(
    vehicle.steeringRadians,
    desiredSteering,
    steeringResponse,
    deltaSeconds,
  );
  const throttle = Math.max(0, clamp(intent.moveZ, -1, 1));
  const brakeRequested = intent.brakeHeld || intent.moveZ < 0;
  // S behaves like a conventional game brake/reverse control: it brakes any
  // forward motion first, then selects reverse once the car has nearly stopped.
  // X remains a direct reverse alternative for existing players and saves.
  const reverseRequested = intent.reverseHeld || intent.moveZ < 0;
  const handbraking = intent.handbrakeHeld;
  const movingForward =
    vehicle.speedMps > ROAD_VEHICLE_TUNING.directionChangeThresholdMps;
  const movingBackward =
    vehicle.speedMps < -ROAD_VEHICLE_TUNING.directionChangeThresholdMps;
  const wetBrakingScale = lerp(
    1,
    ROAD_VEHICLE_TUNING.fullWetBrakingScale,
    wetness01,
  );
  const serviceBrakeAcceleration =
    tuning.brakingMps2 * wetBrakingScale * surfaceGrip;
  let targetSpeed = 0;
  let acceleration: number = tuning.coastingDragMps2;
  let serviceBrakeActive = false;
  if (handbraking) {
    acceleration = tuning.handbrakeDecelerationMps2;
  } else if (reverseRequested) {
    if (movingForward) {
      serviceBrakeActive = true;
      acceleration = serviceBrakeAcceleration;
    } else {
      targetSpeed = -tuning.maximumReverseSpeedMps;
      acceleration = tuning.reverseAccelerationMps2 * gripScale;
    }
  } else if (throttle > 0) {
    if (movingBackward) {
      serviceBrakeActive = true;
      acceleration = serviceBrakeAcceleration;
    } else {
      targetSpeed = throttle * tuning.maximumForwardSpeedMps;
      acceleration = tuning.accelerationMps2 * gripScale;
    }
  } else if (brakeRequested) {
    serviceBrakeActive = true;
    acceleration = serviceBrakeAcceleration;
  }
  let speedMps = moveToward(vehicle.speedMps, targetSpeed, acceleration * deltaSeconds);
  const rawYawRate =
    Math.tan(steeringRadians) * speedMps / spec.wheelbaseM *
    (handbraking ? tuning.handbrakeSteeringScale : 1);
  const yawRate = clamp(
    rawYawRate,
    -tuning.maximumYawRateRadiansPerSecond,
    tuning.maximumYawRateRadiansPerSecond,
  );
  const yawRadians = wrapRadians(
    vehicle.yawRadians + yawRate * deltaSeconds,
  );
  const proposedPosition = {
    x: vehicle.position.x + Math.sin(yawRadians) * speedMps * deltaSeconds,
    y: vehicle.position.y,
    z: vehicle.position.z + Math.cos(yawRadians) * speedMps * deltaSeconds,
  };
  const resolved = queries.resolveMotion({
    vehicleId: vehicle.id,
    currentPosition: vehicle.position,
    proposedPosition,
    currentYawRadians: vehicle.yawRadians,
    proposedYawRadians: yawRadians,
    currentPitchRadians: vehicle.pitchRadians,
    currentRollRadians: vehicle.rollRadians,
    maximumStepHeightM: ROAD_VEHICLE_TUNING.maximumCurbStepM,
    maximumGroundTiltRadians: ROAD_VEHICLE_TUNING.maximumGroundTiltRadians,
    currentSurfaceKind: vehicle.surfaceKind,
    currentSurfaceId: vehicle.surfaceId,
  });
  const impactSpeedMps = resolved.blocked ? Math.abs(speedMps) : 0;
  if (resolved.blocked) {
    speedMps = 0;
  }
  const longitudinalAccelerationMps2 =
    (speedMps - vehicle.speedMps) / deltaSeconds;
  const targetVisualPitch = clamp(
    -longitudinalAccelerationMps2 /
      Math.max(tuning.accelerationMps2, tuning.brakingMps2) *
      ROAD_VEHICLE_TUNING.maximumVisualBodyPitchRadians,
    -ROAD_VEHICLE_TUNING.maximumVisualBodyPitchRadians,
    ROAD_VEHICLE_TUNING.maximumVisualBodyPitchRadians,
  );
  const targetVisualRoll = clamp(
    -yawRate * Math.abs(speedMps) /
      Math.max(1, tuning.maximumForwardSpeedMps) *
      ROAD_VEHICLE_TUNING.maximumVisualBodyRollRadians,
    -ROAD_VEHICLE_TUNING.maximumVisualBodyRollRadians,
    ROAD_VEHICLE_TUNING.maximumVisualBodyRollRadians,
  );
  const suspensionTarget =
    ROAD_VEHICLE_TUNING.suspensionTravelM *
    (vehicle.surfaceKind === "terrain" ? 0.5 : handbraking ? 0.2 : 0.08);
  const impactIntensity01 = impactSpeedMps <= ROAD_VEHICLE_TUNING.impactDamageSpeedMps
    ? vehicle.impactIntensity01
    : clamp(
        impactSpeedMps /
          ROAD_VEHICLE_TUNING.maximumDamageImpactSpeedMps,
        0,
        1,
      );
  const damage01 = resolved.blocked
    ? clamp(
        vehicle.damage01 +
          Math.max(
            0,
            impactSpeedMps - ROAD_VEHICLE_TUNING.impactDamageSpeedMps,
          ) /
            ROAD_VEHICLE_TUNING.maximumDamageImpactSpeedMps,
        0,
        1,
      )
    : vehicle.damage01;
  const nextSurfaceKind = resolved.surfaceKind ?? vehicle.surfaceKind;
  const nextSurfaceId = resolved.surfaceId === undefined
    ? vehicle.surfaceId
    : resolved.surfaceId;
  const safePose =
    !resolved.blocked &&
      nextSurfaceKind !== "terrain" &&
      Math.abs(resolved.pitchRadians) < 0.2 &&
      Math.abs(resolved.rollRadians) < 0.2;
  return {
    ...vehicle,
    position: { ...resolved.position },
    yawRadians: resolved.yawRadians,
    pitchRadians: resolved.pitchRadians,
    rollRadians: resolved.rollRadians,
    speedMps,
    steeringRadians,
    longitudinalAccelerationMps2,
    wheelRotationRadians: wrapRadians(
      vehicle.wheelRotationRadians +
        speedMps * deltaSeconds / spec.wheelRadiusM,
    ),
    visualPitchRadians: smoothToward(
      vehicle.visualPitchRadians,
      targetVisualPitch,
      ROAD_VEHICLE_TUNING.bodyMotionResponsePerSecond,
      deltaSeconds,
    ),
    visualRollRadians: smoothToward(
      vehicle.visualRollRadians,
      targetVisualRoll,
      ROAD_VEHICLE_TUNING.bodyMotionResponsePerSecond,
      deltaSeconds,
    ),
    suspensionCompressionM: smoothToward(
      vehicle.suspensionCompressionM,
      suspensionTarget,
      ROAD_VEHICLE_TUNING.bodyMotionResponsePerSecond,
      deltaSeconds,
    ),
    surfaceKind: nextSurfaceKind,
    surfaceId: nextSurfaceId,
    brakeLightsOn:
      (serviceBrakeActive || handbraking) && Math.abs(vehicle.speedMps) > 0.05,
    reverseLightsOn:
      (reverseRequested && !movingForward) || speedMps < -0.05,
    hornActive: intent.hornHeld,
    lookBehindActive: intent.lookBehindHeld,
    handbrakeActive: handbraking,
    impactIntensity01,
    damage01,
    lastSafePosition: safePose
      ? { ...resolved.position }
      : vehicle.lastSafePosition,
    lastSafeYawRadians: safePose
      ? resolved.yawRadians
      : vehicle.lastSafeYawRadians,
  };
}

function resultForMounted(
  state: RoadVehicleFleetState,
  vehicle: Readonly<RoadVehicleState>,
  spec: Readonly<RoadVehicleSpec>,
  seatId: string,
  transition: RoadVehicleFleetStepResult["transition"],
  interactionConsumed: boolean,
): RoadVehicleFleetStepResult {
  const seat = spec.seats.find((entry) => entry.id === seatId);
  if (seat === undefined) {
    throw new Error(`Seat ${seatId} is missing from ${spec.id}`);
  }
  const occupantPlayerId = vehicle.occupantBySeatId[seatId];
  const mount = occupantPlayerId === undefined
    ? undefined
    : state.mountByPlayerId[occupantPlayerId];
  const seatPosition = localToWorld(
    vehicle.position,
    vehicle.yawRadians,
    seat.localPosition,
  );
  let position = seatPosition;
  if (mount?.transition !== undefined) {
    const progress01 = smoothstep(
      0,
      1,
      1 - mount.transition.secondsRemaining /
        ROAD_VEHICLE_TUNING.occupantTransitionSeconds,
    );
    const start = mount.transition.kind === "entering"
      ? mount.transition.startPosition
      : seatPosition;
    const end = mount.transition.kind === "entering"
      ? seatPosition
      : mount.transition.endPosition;
    position = {
      x: lerp(start.x, end.x, progress01),
      y: lerp(start.y, end.y, progress01),
      z: lerp(start.z, end.z, progress01),
    };
  }
  return {
    state,
    transition,
    interactionConsumed,
    blockedExit: false,
    playerOverride: {
      position,
      facingYaw: vehicle.yawRadians,
      surfaceId: `vehicle:${vehicle.id}:${seat.id}`,
      vehicleId: vehicle.id,
      seatRole: seat.role,
    },
  };
}

function updateMountedState(
  state: Readonly<RoadVehicleFleetState>,
  playerId: PlayerId,
  vehicle: RoadVehicleState,
  seatId: string,
  tick: number,
): RoadVehicleFleetState {
  return {
    ...state,
    tick,
    vehiclesById: { ...state.vehiclesById, [vehicle.id]: vehicle },
    mountByPlayerId: {
      ...state.mountByPlayerId,
      [playerId]: {
        ...state.mountByPlayerId[playerId],
        vehicleId: vehicle.id,
        seatId,
      },
    },
  };
}

function unchanged(state: Readonly<RoadVehicleFleetState>, tick: number): RoadVehicleFleetStepResult {
  return {
    state: { ...state, tick },
    transition: null,
    interactionConsumed: false,
    blockedExit: false,
    playerOverride: null,
  };
}

function nearestEnterableVehicle(
  state: Readonly<RoadVehicleFleetState>,
  player: Readonly<{ x: number; z: number }>,
): { vehicle: RoadVehicleState; spec: RoadVehicleSpec; distanceM: number } | null {
  let nearest: { vehicle: RoadVehicleState; spec: RoadVehicleSpec; distanceM: number } | null = null;
  for (const spec of roadVehicleSpecs(state)) {
    const vehicle = state.vehiclesById[spec.id];
    if (vehicle === undefined || Object.keys(vehicle.occupantBySeatId).length >= spec.seats.length) continue;
    const distanceM = horizontalDistanceToVehicleBody(spec, vehicle, player);
    if (distanceM <= ROAD_VEHICLE_TUNING.interactionRadiusM && (nearest === null || distanceM < nearest.distanceM)) {
      nearest = { vehicle, spec, distanceM };
    }
  }
  return nearest;
}

function horizontalDistanceToVehicleBody(
  spec: Readonly<RoadVehicleSpec>,
  vehicle: Readonly<RoadVehicleState>,
  point: Readonly<{ x: number; z: number }>,
): number {
  const deltaX = point.x - vehicle.position.x;
  const deltaZ = point.z - vehicle.position.z;
  const cosine = Math.cos(vehicle.yawRadians);
  const sine = Math.sin(vehicle.yawRadians);
  const localX = deltaX * cosine - deltaZ * sine;
  const localZ = deltaX * sine + deltaZ * cosine;
  const outsideX = Math.max(0, Math.abs(localX) - spec.bodySizeM.x / 2);
  const outsideZ = Math.max(0, Math.abs(localZ) - spec.bodySizeM.z / 2);
  return Math.hypot(outsideX, outsideZ);
}

function nearestAvailableSeat(
  spec: Readonly<RoadVehicleSpec>,
  vehicle: Readonly<RoadVehicleState>,
  player: Readonly<{ x: number; y: number; z: number }>,
) {
  return spec.seats
    .filter((seat) => vehicle.occupantBySeatId[seat.id] === undefined)
    .map((seat) => ({ seat, position: localToWorld(vehicle.position, vehicle.yawRadians, seat.localPosition) }))
    .sort((left, right) =>
      Math.hypot(left.position.x - player.x, left.position.z - player.z) -
      Math.hypot(right.position.x - player.x, right.position.z - player.z)
    )[0]?.seat ?? null;
}

function nextAvailableSeat(spec: Readonly<RoadVehicleSpec>, vehicle: Readonly<RoadVehicleState>, currentSeatId: string) {
  const start = spec.seats.findIndex((seat) => seat.id === currentSeatId);
  for (let offset = 1; offset <= spec.seats.length; offset += 1) {
    const seat = spec.seats[(start + offset) % spec.seats.length];
    if (seat !== undefined && (vehicle.occupantBySeatId[seat.id] === undefined || seat.id === currentSeatId)) return seat;
  }
  return null;
}

export function localToWorld(
  origin: Readonly<{ x: number; y: number; z: number }>,
  yawRadians: number,
  local: Readonly<{ x: number; y: number; z: number }>,
) {
  const cosine = Math.cos(yawRadians);
  const sine = Math.sin(yawRadians);
  return {
    x: origin.x + local.x * cosine + local.z * sine,
    y: origin.y + local.y,
    z: origin.z - local.x * sine + local.z * cosine,
  };
}

function moveToward(current: number, target: number, delta: number): number {
  return Math.abs(target - current) <= delta ? target : current + Math.sign(target - current) * delta;
}

function smoothToward(
  current: number,
  target: number,
  responsePerSecond: number,
  deltaSeconds: number,
): number {
  return lerp(
    current,
    target,
    1 - Math.exp(-Math.max(0, responsePerSecond) * deltaSeconds),
  );
}

function lerp(from: number, to: number, alpha: number): number {
  return from + (to - from) * alpha;
}

function smoothstep(start: number, end: number, value: number): number {
  if (end <= start) return value >= end ? 1 : 0;
  const t = clamp((value - start) / (end - start), 0, 1);
  return t * t * (3 - 2 * t);
}

function sideForLocalX(localX: number): RoadVehicleDoorSide {
  return localX <= 0 ? "left" : "right";
}

function seatIdPositionX(
  spec: Readonly<RoadVehicleSpec>,
  seatId: string,
): number {
  return spec.seats.find((seat) => seat.id === seatId)?.localPosition.x ?? -1;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function roadVehiclePresentations(state: Readonly<RoadVehicleFleetState>): readonly RoadVehiclePresentation[] {
  return Object.values(state.vehiclesById).map((vehicle) => ({
    id: vehicle.id,
    position: vehicle.position,
    yawRadians: vehicle.yawRadians,
    pitchRadians: vehicle.pitchRadians,
    rollRadians: vehicle.rollRadians,
    speedMps: vehicle.speedMps,
    steeringRadians: vehicle.steeringRadians,
    longitudinalAccelerationMps2: vehicle.longitudinalAccelerationMps2,
    wheelRotationRadians: vehicle.wheelRotationRadians,
    visualPitchRadians: vehicle.visualPitchRadians,
    visualRollRadians: vehicle.visualRollRadians,
    suspensionCompressionM: vehicle.suspensionCompressionM,
    surfaceKind: vehicle.surfaceKind,
    headlightsOn: vehicle.headlightsOn,
    brakeLightsOn: vehicle.brakeLightsOn,
    reverseLightsOn: vehicle.reverseLightsOn,
    leftIndicatorOn: vehicle.leftIndicatorOn,
    rightIndicatorOn: vehicle.rightIndicatorOn,
    beaconOn: vehicle.beaconOn,
    sirenOn: vehicle.sirenOn,
    hornActive: vehicle.hornActive,
    lookBehindActive: vehicle.lookBehindActive,
    handbrakeActive: vehicle.handbrakeActive,
    activeDoorSide: vehicle.activeDoorSide,
    doorOpen01: clamp(
      vehicle.doorOpenSecondsRemaining /
        ROAD_VEHICLE_TUNING.doorTransitionSeconds,
      0,
      1,
    ),
    indicatorPhase01:
      ((state.tick / 60) % ROAD_VEHICLE_TUNING.indicatorPeriodSeconds) /
      ROAD_VEHICLE_TUNING.indicatorPeriodSeconds,
    impactIntensity01: vehicle.impactIntensity01,
    damage01: vehicle.damage01,
  }));
}

export function mountedRoadVehicle(state: Readonly<RoadVehicleFleetState>, playerId: PlayerId) {
  const mount = state.mountByPlayerId[playerId];
  if (mount === undefined) return null;
  const vehicle = state.vehiclesById[mount.vehicleId];
  const spec = roadVehicleSpecFor(state, mount.vehicleId);
  const seat = spec.seats.find((entry) => entry.id === mount.seatId);
  return vehicle === undefined || seat === undefined ? null : { mount, vehicle, spec, seat };
}

export function roadVehiclePlayerOverride(
  state: Readonly<RoadVehicleFleetState>,
  playerId: PlayerId,
): NonNullable<RoadVehicleFleetStepResult["playerOverride"]> | null {
  const mounted = mountedRoadVehicle(state, playerId);
  return mounted === null
    ? null
    : {
        position: localToWorld(
          mounted.vehicle.position,
          mounted.vehicle.yawRadians,
          mounted.seat.localPosition,
        ),
        facingYaw: mounted.vehicle.yawRadians,
        surfaceId: `vehicle:${mounted.vehicle.id}:${mounted.seat.id}`,
        vehicleId: mounted.vehicle.id,
        seatRole: mounted.seat.role,
      };
}

export function roadVehicleInteractionPrompt(
  state: Readonly<RoadVehicleFleetState>,
  playerId: PlayerId,
  playerPosition: Readonly<{ x: number; z: number }>,
): string | null {
  const mounted = mountedRoadVehicle(state, playerId);
  if (mounted !== null) {
    if (Math.abs(mounted.vehicle.speedMps) > ROAD_VEHICLE_TUNING.minimumExitSpeedMps) {
      return `${mounted.seat.role === "driver" ? "W throttle · S brake/reverse · X reverse · A/D steer · Space handbrake · V rear view · " : "Passenger · "}${Math.abs(mounted.vehicle.speedMps * 3.6).toFixed(0)} km/h`;
    }
    const emergencyControls = mounted.spec.hasEmergencyEquipment
      ? " · J beacons · K siren"
      : "";
    return `${mounted.seat.role === "driver" ? `Driver · W throttle · S brake/reverse · X reverse · A/D steer · Space handbrake · H horn · T lights · Z/C indicators${emergencyControls} · V rear view · Backspace recover` : "Passenger"} · R change seat · E exit`;
  }
  const candidate = nearestEnterableVehicle(state, playerPosition);
  return candidate === null
    ? null
    : `${candidate.spec.vehicleClass === "car" ? "Enter" : "Board"} ${candidate.spec.label}`;
}

export function applyRoadVehiclePlayerTick(
  state: Readonly<GameState>,
  intent: Readonly<InputIntent>,
  override: NonNullable<RoadVehicleFleetStepResult["playerOverride"]>,
  deltaSeconds: number,
): GameState {
  return placePlayerInRoadVehicle(
    {
      ...state,
      tick: intent.tick,
      simulationTimeSeconds: state.simulationTimeSeconds + deltaSeconds,
    },
    intent.playerId,
    intent.lookYaw,
    override,
  );
}

export function placePlayerInRoadVehicle(
  state: Readonly<GameState>,
  playerId: PlayerId,
  lookYaw: number,
  override: NonNullable<RoadVehicleFleetStepResult["playerOverride"]>,
): GameState {
  const player = state.playersById[playerId];
  if (player === undefined) throw new Error(`Player ${playerId} is missing from game state`);
  return {
    ...state,
    playersById: {
      ...state.playersById,
      [playerId]: {
        ...player,
        position: { ...override.position },
        velocity: { x: 0, y: 0, z: 0 },
        facingYaw: override.facingYaw,
        lookYaw,
        isSprinting: false,
        locomotion: {
          ...player.locomotion,
          mode: "grounded",
          grounded: true,
          groundNormal: { x: 0, y: 1, z: 0 },
          groundSlopeRadians: 0,
          surfaceId: override.surfaceId,
          coyoteTicksRemaining: 0,
          jumpBufferTicksRemaining: 0,
          landingResponseTicksRemaining: 0,
          mantleProgress: 0,
          footstepDistanceMetres: 0,
          lastStepHeightMetres: 0,
        },
      },
    },
  };
}
